namespace $ {
	const $shm_hitalama_map_history_maps = new WeakMap< $shm_hitalama_map_history, $shm_hitalama_map >

	/** A tiny map-local pointer which lets cold readers bypass the legacy history root. */
	export class $shm_hitalama_map_history_reference extends $giper_baza_entity.with({
		History: $giper_baza_atom_link.to( ()=> $shm_hitalama_map_history ),
	}) {}

	export class $shm_hitalama_map_history extends $giper_baza_entity.with({
		Map: $giper_baza_atom_link.to( ()=> $shm_hitalama_map ),
		Events: $shm_hitalama_map_history_events,
		By_target: $shm_hitalama_map_history_targets,
		Storage_version: $giper_baza_atom_real,
		Legacy_migrated: $giper_baza_atom_bool,
		Storage_writers: $shm_hitalama_map_history_storage_writers,
		Storage_indices: $shm_hitalama_map_history_storage_indices,
	}) {

		static readonly storage_version = 2
		static readonly storage_page_size = 256
		static readonly storage_page_byte_limit = 1024 * 1024
		static readonly storage_index_shards = 64
		static readonly storage_index_target_limit = 32
		static readonly storage_recent_size = 64

		/** The map-wide scope already includes every narrower scope. */
		static target_links( targets: readonly ( string | $giper_baza_pawn )[] ) {
			const links = [ ...new Set( targets.map( target => typeof target === 'string' ? target : target.link().str ) ) ]
			return links.includes( '__all__' ) ? [ '__all__' ] : links
		}

		map() {
			return $shm_hitalama_map_history_maps.get( this )
				?? this.Map()?.remote()
				?? this.land().Data( $shm_hitalama_map )
		}

		map_bind( map: $shm_hitalama_map, persist = true ) {
			$shm_hitalama_map_history_maps.set( this, map )
			if( persist && this.Map()?.remote()?.link().str !== map.link().str ) this.Map( null )?.remote( map )
			return map
		}

		private storage_meta() {
			const land = this.land().area_make( $mol_hash_string( `${ this.link().str }:history-meta` ) )
			return land.Data( $shm_hitalama_map_history_storage_meta )
		}

		storage_migrated( next?: boolean ) {
			const meta = this.storage_meta()
			if( next !== undefined ) {
				meta.version( $shm_hitalama_map_history.storage_version )
				meta.legacy_migrated( next )
				if( next ) meta.writers_migrated( true )
			}
			return meta.legacy_migrated()
		}

		storage_ready() {
			const meta = this.storage_meta()
			return meta.version() === $shm_hitalama_map_history.storage_version
				&& meta.legacy_migrated()
				&& meta.writers_migrated()
		}

		events() {
			return this.events_for( '__all__' )
		}

		events_for( target: string | $giper_baza_pawn ) {
			this.keys()
			const link = typeof target === 'string' ? target : target.link().str
			const events = new Map< string, $shm_hitalama_map_history_event >
			if( !this.Legacy_migrated()?.val() ) {
				for( const event of this.events_legacy_for( link ) ) events.set( event.link().str, event )
			}
			for( const summary of this.storage_summaries_all([ link ]) ) {
				const event = this.storage_event( summary.e )
				events.set( event.link().str, event )
			}
			return [ ... events.values() ].sort( ( left, right )=> left.time() - right.time() )
		}

		events_page_for(
			targets: readonly ( string | $giper_baza_pawn )[],
			limit = 10,
			offset = 0,
		): $shm_hitalama_map_history_page_result {
			this.keys()
			const links = $shm_hitalama_map_history.target_links( targets )
			if( !links.length || limit <= 0 ) return { count: 0, events: [] }

			const legacy = new Map< string, $shm_hitalama_map_history_event >
			if( !this.Legacy_migrated()?.val() ) {
				for( const target of links ) for( const event of this.events_legacy_for( target ) ) legacy.set( event.link().str, event )
			}
			const summaries = links.length === 1
				? this.storage_summaries_page( links[ 0 ], Math.max( 0, offset ) + limit )
				: ( ()=> {
					const items = this.storage_summaries_all( links )
					return { count: items.length, items }
				} )()
			const events = new Map( legacy )
			for( const summary of summaries.items ) events.set( summary.e, this.storage_event( summary.e ) )
			const ordered = [ ... events.values() ].sort( ( left, right )=> right.time() - left.time() )
			const count = links.length === 1 && legacy.size === 0 ? summaries.count : ordered.length
			return { count, events: ordered.slice( offset, offset + limit ) }
		}

		/** Exact count without summary/payload pages for a migrated single scope.
		 * Overlapping scopes and legacy storage keep the existing deduplicating reader.
		 */
		entries_count_for( targets: readonly ( string | $giper_baza_pawn )[] ) {
			const links = $shm_hitalama_map_history.target_links( targets )
			if( !links.length ) return 0
			if( links.length !== 1 || !this.storage_ready() ) return this.entries_page_for( links, 1 ).count
			const writers = this.storage_target( links[0] )?.Writers()
			let count = 0
			for( const key of writers?.keys() ?? [] ) count += writers!.key( key )!.count()
			return count
		}

		entries_page_retained_for(
			targets: readonly ( string | $giper_baza_pawn )[],
			limit = 10,
			offset = 0,
		): $shm_hitalama_map_history_entry_page_result {
			const links = $shm_hitalama_map_history.target_links( targets )
			if( links.length !== 1 ) return this.entries_page_for( links, limit, offset )
			return this.entries_page_retained( JSON.stringify([ links[ 0 ], limit, offset ]) )
		}

		@ $mol_mem_key
		private entries_page_retained( key: string ) {
			const [ target, limit, offset, peer ] = JSON.parse( key ) as [ string, number, number, string? ]
			return this.entries_page_for([ target ], limit, offset, peer )
		}

		/** Undo discovery skips other authors' physical streams, not their payloads. */
		entries_page_retained_peer( peer: string, limit = 64, offset = 0 ) {
			return this.entries_page_retained( JSON.stringify([ '__all__', limit, offset, peer ]) )
		}

		entries_page_for(
			targets: readonly ( string | $giper_baza_pawn )[],
			limit = 10,
			offset = 0,
			peer?: string,
		): $shm_hitalama_map_history_entry_page_result {
			const links = $shm_hitalama_map_history.target_links( targets )
			if( !links.length || limit <= 0 ) return { count: 0, events: [] }
			offset = Math.max( 0, offset )
			// Only the compatibility path needs the full merge. Legacy events can
			// be newer than indexed events, and a partially completed migration can
			// expose the same command under both its old and relocated links.
			const legacy = this.storage_migrated() ? [] : this.events_legacy_page( links )
			if( legacy.length ) {
				const selected = new Map< string, $shm_hitalama_map_history_summary >
				for( const event of legacy ) {
					const data = event.data()
					if( !data ) continue
					selected.set( data.id, this.storage_summary( event, data, event.time(), event.author(), event.peer(), event.targets() ) )
				}
				// Prefer the indexed link: undo/redo origins in the migrated stream
				// already refer to the relocated payload, not its obsolete copy.
				for( const summary of this.storage_summaries_all( links ) ) selected.set( summary.i, summary )
				const ordered = [ ...selected.values() ].sort( ( left, right )=> right.t - left.t )
				return {
					count: ordered.length,
					events: ordered.slice( offset, offset + limit ).map( summary=> new $shm_hitalama_map_history_storage_entry( this, summary ) ),
				}
			}
			const page = links.length === 1
				? this.storage_summaries_page( links[ 0 ], offset + limit, peer )
				: ( ()=> {
					const items = this.storage_summaries_all( links ).reverse()
					return { count: items.length, items }
				} )()
			const summaries = page.items.slice( offset, offset + limit )
			const events: $shm_hitalama_map_history_storage_entry[] = summaries.map( summary => (
					new $shm_hitalama_map_history_storage_entry( this, summary )
				) )
			return {
				count: page.count,
				events,
			}
		}

		static scope_target( target: string | $giper_baza_pawn ) {
			const link = typeof target === 'string' ? target : target.link().str
			return `scope:${ link }`
		}

		events_for_scope( target: string | $giper_baza_pawn ) {
			return this.events_for( $shm_hitalama_map_history.scope_target( target ) )
		}

		event_area( time: number ) {
			const day = Math.floor( time / 86_400_000 )
			return this.land().area_make( $mol_hash_string( `history-events:${ this.link().str }:${ day }` ) )
		}

		event_pawn( time: number, id: string ) {
			const land = this.event_area( time )
			let idea = $mol_hash_string( id )
			while( true ) {
				idea = $mol_hash_numbers([ idea ])
				if( !idea ) continue
				const head = $giper_baza_link.from_int( idea )
				if( /[æÆ]/.test( head.str ) ) continue
				return land.Pawn( $shm_hitalama_map_history_event ).Head( head )
			}
		}

		private storage_writer( writer: string, auto = false ) {
			const land = this.land().area_make( $mol_hash_string( `${ this.link().str }:history-writer:${ writer }` ) )
			const state = land.Data( $shm_hitalama_map_history_storage_writer )
			state.keys()
			if( auto && !state.keys().length && !this.storage_meta().writers_migrated() ) {
				this.storage_writer_import( writer, state )
			}
			return state
		}

		private storage_writer_import( writer: string, state = this.storage_writer( writer ) ) {
			const legacy = this.Storage_writers()?.key( writer )
			if( !legacy ) return state
			const cursor = legacy.cursor()
			const recent = legacy.recent()
			if( cursor.sequence || cursor.count || cursor.page || cursor.bytes ) state.cursor( cursor )
			if( recent.length ) state.recent( recent )
			return state
		}

		storage_writers_migrate() {
			const writers = this.Storage_writers()
			writers?.keys()
			for( const writer of writers?.keys() ?? [] ) this.storage_writer_import( String( writer ) )
			this.storage_meta().writers_migrated( true )
			return writers?.keys().length ?? 0
		}

		private storage_index_shard( target: string, auto = false, commands = false ) {
			const hash = $mol_hash_string( target ) >>> 0
			const shard = String( hash % $shm_hitalama_map_history.storage_index_shards )
			// Index shard links are deterministic. Reading them through the registry
			// on the history root made every cold query download the whole legacy
			// history land merely to discover a link we can calculate locally.
			let path = ''
			for( let depth = 6; depth < 128; ++depth ) {
				const suffix = path ? `:${ path }` : ''
				const namespace = commands ? 'history-commands' : 'history-index'
				const land = this.land().area_make( $mol_hash_string( `${ this.link().str }:${ namespace }:${ shard }${ suffix }` ) )
				const index = land.Data( $shm_hitalama_map_history_storage_index )
				index.units()
				// A cold writer must not select an apparently empty root before its
				// saved keys and routing flag arrive. Reads remain reactive/lazy.
				if( auto ) $shm_hitalama_map_history_storage_ready.settled( index )
				const targets = commands ? index.Commands() : index.Targets()
				targets?.units()
				// Existing entries stay at their original address. Splitting never
				// snapshots or relocates concurrently changing writer counters.
				if( targets?.key( target ) ) return index
				const routed = index.Routing()?.val() ?? false
				if( !routed ) {
					if( !auto || ( targets?.keys().length ?? 0 ) < $shm_hitalama_map_history.storage_index_target_limit ) return index
					this.storage_index_route_enable( index )
				}
				const block = Math.floor( depth / 32 )
				const bits = block ? $mol_hash_string( `${ target }:history-route:${ block }` ) >>> 0 : hash
				path += String( ( bits >>> ( depth % 32 ) ) & 1 )
			}
			throw new Error( 'Слишком много совпадающих ключей индекса истории' )
		}

		@ $mol_action
		private storage_index_route_enable( index: $shm_hitalama_map_history_storage_index ) {
			if( !index.Routing()?.val() ) index.Routing( null )!.val( true )
		}

		private storage_target( target: string, auto = false ) {
			const index = this.storage_index_shard( target, auto )
			if( !index ) return null
			let targets = index.Targets()
			// Open the same ordered/decrypted units, without decoding every key
			// into an unused array before the targeted lookup below.
			targets?.units()
			if( !targets && auto ) {
				index.Targets( null )
				targets = index.Targets()
			}
			if( !targets ) return null
			let state = targets.key( target )
			if( !state && auto ) {
				targets.key( target, 'auto' )
				state = targets.key( target )
			}
			return state ?? null
		}

		private storage_command( peer: string, id: string, event?: $shm_hitalama_map_history_event ) {
			const key = JSON.stringify([ peer, id ])
			const index = this.storage_index_shard( key, Boolean( event ), true )
			const commands = event ? index.Commands( null )! : index.Commands()
			const reference = event ? commands!.key( key, 'auto' )! : commands?.key( key )
			const stored = reference?.val()
			if( stored ) return this.storage_event( stored.str )
			if( event ) reference!.val( event.link() )
			return event ?? null
		}

		@ $mol_action
		private storage_command_resume( event: $shm_hitalama_map_history_event ) {
			const data = event.data()
			if( !data ) throw new Error( 'Не загружены данные повторной команды истории' )
			return this.storage_index_event( event, data, event.time(), event.author(), event.peer(), event.targets() )
		}

		private storage_summary_page( writer: string, page: number ) {
			const land = this.land().area_make( $mol_hash_string( `${ this.link().str }:history-summary:${ writer }:${ page }` ) )
			return land.Data( $shm_hitalama_map_history_storage_page )
		}

		private storage_payload_land( writer: string, page: number ) {
			return this.land().area_make( $mol_hash_string( `${ this.link().str }:history-payload:${ writer }:${ page }` ) )
		}

		event_by_link( link: string ) {
			return this.$.$giper_baza_glob.Pawn(
				new this.$.$giper_baza_link( link ),
				this.$.$shm_hitalama_map_history_event,
			)
		}

		private storage_event( link: string ) {
			return this.event_by_link( link )
		}

		private events_legacy_for( target: string ) {
			if( target === '__all__' ) return this.Events()?.remote_list() ?? []
			const targets = this.By_target()
			targets?.keys()
			return targets?.key( target )?.remote_list() ?? []
		}

		private events_legacy_page( targets: readonly string[] ) {
			if( targets.length === 1 ) return this.events_legacy_for( targets[ 0 ] )
			const selected = new Set< string >
			for( const target of targets ) {
				for( const event of this.events_legacy_for( target ) ) selected.add( event.link().str )
			}
			return ( this.Events()?.remote_list() ?? [] ).filter( event => selected.has( event.link().str ) )
		}

		private storage_summary(
			event: $shm_hitalama_map_history_event,
			data: $shm_hitalama_map_history_data,
			time: number,
			author: string,
			peer: string,
			targets: readonly string[],
		): $shm_hitalama_map_history_summary {
			const scopes = [ ... new Set([ '__all__', ... targets ]) ]
			return {
				e: event.link().str,
				i: data.id,
				t: time,
				a: author,
				p: peer,
				s: data.title.slice( 0, 512 ),
				k: [ ... new Set( data.changes.map( change => change.type ) ) ],
				g: scopes.slice( 0, 128 ),
				... scopes.length > 128 ? { g_more: true } : {},
				d: this.storage_preview( data ),
			}
		}

		private storage_summary_targets( summary: $shm_hitalama_map_history_summary ) {
			return summary.g_more ? [ '__all__', ...this.storage_event( summary.e ).targets() ] : summary.g
		}

		private storage_preview( data: $shm_hitalama_map_history_data ): $shm_hitalama_map_history_data {
			// One budget for the entire command, not a per-branch limit. Otherwise
			// twenty changes with twenty nested fields multiply a tiny leaf preview
			// into megabytes. This never mutates the complete undo payload.
			let remaining = 4096
			const cost = ( input: any )=> $mol_charset_encode( JSON.stringify( input ) ?? 'null' ).byteLength
			const value = ( input: any, depth = 0 ): any => {
				if( remaining < 8 ) return null
				if( typeof input === 'string' ) {
					const limit = Math.min( depth >= 3 ? 160 : 320, Math.floor( ( remaining - 4 ) / 6 ) )
					const text = input.length > limit ? `${ input.slice( 0, limit ) }…` : input
					remaining -= cost( text )
					return text
				}
				if( !input || typeof input !== 'object' ) {
					const bytes = cost( input )
					if( bytes > remaining ) { remaining -= 5; return '…' }
					remaining -= bytes
					return input
				}
				if( depth >= 3 ) { remaining -= 5; return '…' }
				remaining -= 2
				if( Array.isArray( input ) ) {
					const items: any[] = []
					for( const item of input.slice( 0, 10 ) ) {
						if( remaining < 8 ) break
						--remaining
						items.push( value( item, depth + 1 ) )
					}
					return items
				}
				const fields: [ string, any ][] = []
				for( const [ key, item ] of Object.entries( input ).slice( 0, 20 ) ) {
					const bytes = cost( key ) + 2
					if( remaining < bytes + 8 ) continue
					remaining -= bytes
					fields.push([ key, value( item, depth + 1 ) ])
				}
				return Object.fromEntries( fields )
			}
			return {
				id: data.id,
				title: data.title.slice( 0, 512 ),
				source: data.source,
				... data.origin ? { origin: data.origin } : {},
				changes: data.changes.slice( 0, 20 ).map( change => ({
					type: change.type,
					target: change.target,
					... change.targets ? { targets: change.targets.slice( 0, 20 ) } : {},
					... Object.hasOwn( change, 'after' ) && remaining >= 8 ? { after: value( change.after ) } : {},
					... change.automatic ? { automatic: true } : {},
					... change.meta && remaining >= 8 ? { meta: value( change.meta ) } : {},
				})),
			}
		}

		private storage_place( event: $shm_hitalama_map_history_event, writer: string, bytes: number ) {
			const stored = event.storage()
			if( stored ) return stored
			const state = this.storage_writer( writer, true )!
			const allocated = state.allocate(
				bytes,
				$shm_hitalama_map_history.storage_page_size,
				$shm_hitalama_map_history.storage_page_byte_limit,
				event.time(),
			)
			const place = { writer, page: allocated.page }
			event.storage( place )
			return place
		}

		private storage_index_event(
			event: $shm_hitalama_map_history_event,
			data: $shm_hitalama_map_history_data,
			time: number,
			author: string,
			peer: string,
			targets: readonly string[],
		) {
			const place = event.storage() ?? this.storage_place(
				event, $shm_hitalama_map_history_runtime.writer_id( peer ), $mol_wire_sync( this ).event_bytes( data, time, author, peer, targets ),
			)
			const page = this.storage_summary_page( place.writer, place.page )
			const summary = this.storage_summary( event, data, time, author, peer, targets )
			let entries = page.Entries()
			if( !entries ) {
				page.Entries( null )
				entries = page.Entries()
			}
			if( !entries ) throw new Error( 'Не удалось открыть страницу индекса истории' )
			if( !page.entries().some( item => item.e === summary.e ) ) entries.splice([ summary ])

			const indexed = new Set( $mol_wire_sync( event ).indexed_targets() )
			for( const target of [ ...new Set([ '__all__', ...targets ]) ] ) {
				if( indexed.has( target ) ) continue
				this.storage_index_target( event, target, place.writer, page, time )
				indexed.add( target )
			}

			const writer = this.storage_writer( place.writer, true )!
			const recent = [ ... writer.recent().filter( item => item.e !== summary.e ), summary ]
			writer.recent( recent.slice( -$shm_hitalama_map_history.storage_recent_size ) )
			return event
		}

		/** Keep completed targets stable while a later target waits for storage. */
		@ $mol_action
		private storage_index_target(
			event: $shm_hitalama_map_history_event,
			target: string,
			writer_id: string,
			page: $shm_hitalama_map_history_storage_page,
			time: number,
		) {
			const state = this.storage_target( target, true )!
			let writers = state.Writers()
			if( !writers ) {
				state.Writers( null )
				writers = state.Writers()
			}
			if( !writers ) throw new Error( 'Не удалось открыть индекс авторов истории' )
			let writer = writers.key( writer_id )
			if( !writer ) {
				writers.key( writer_id, 'auto' )
				writer = writers.key( writer_id )
			}
			if( !writer ) throw new Error( 'Не удалось открыть индекс автора истории' )
			writer.page_add( page, time )
			// Preserve the legacy plan and atomic receipt for independently
			// suspended commands; do not change counters or storage contracts.
			event.index_count_plan( target, $mol_wire_sync( writer ).count() + 1 )
			writer.count_event_apply( event, target )
			event.indexed_target_mark( target )
		}

		private storage_streams( target: string, peer?: string ) {
			const state = this.storage_target( target )
			if( !state ) return []
			return ( state.Writers()?.keys() ?? [] ).filter( writer => {
				if( peer === undefined ) return true
				// Unknown/older writer names stay on the conservative path. Only
				// canonical peer, legacy:peer and session:peer:nonce can be skipped.
				const match = String(writer).match(/^(?:session:([a-zæA-ZÆ0-9]{8}):[^:]+|legacy:([a-zæA-ZÆ0-9]{8})|([a-zæA-ZÆ0-9]{8}))$/)
				const owner = match?.[1] ?? match?.[2] ?? match?.[3]
				return owner === undefined || owner === peer
			}).map( writer => {
				const value = state.Writers()!.key( writer )!
				return {
					writer: String( writer ),
					count: value.count(),
					max_time: value.refs()?.max_time ?? Number.POSITIVE_INFINITY,
					pages: value.pages_reverse(),
					bounded_pages: value.pages_bounded_reverse(),
				}
			} )
		}

		private storage_summaries_all( targets: readonly string[] ) {
			const pages = new Map< string, $shm_hitalama_map_history_storage_page >
			for( const target of targets ) for( const stream of this.storage_streams( target ) ) {
				for( const page of stream.pages ) pages.set( page.link().str, page )
			}
			const selected = new Map< string, $shm_hitalama_map_history_summary >
			const target_set = new Set( targets )
			for( const page of pages.values() ) for( const summary of page.entries() ) {
				if( !summary.g.some( target => target_set.has( target ) ) && !this.storage_summary_targets( summary ).some( target=> target_set.has( target ) ) ) continue
				selected.set( summary.e, summary )
			}
			return [ ... selected.values() ].sort( ( left, right )=> left.t - right.t )
		}

		private storage_summaries_page( target: string, wanted: number, peer?: string ) {
			const streams = this.storage_streams( target, peer ).map( stream => ({
				... stream,
				pending: stream.count > 0,
				bound: stream.max_time ?? Number.POSITIVE_INFINITY,
				position: 0,
			}) )
			const count = streams.reduce( ( sum, stream )=> sum + stream.count, 0 )
			const selected = new Map< string, $shm_hitalama_map_history_summary >
			const positions = new Map< string, { writer: string, position: number } >
			const compare = ( left: $shm_hitalama_map_history_summary, right: $shm_hitalama_map_history_summary )=> {
				const time = right.t - left.t
				if( time ) return time
				const a = positions.get( left.e )!, b = positions.get( right.e )!
				return a.writer < b.writer ? -1 : a.writer > b.writer ? 1 : a.position - b.position
			}
			const load = ( stream: typeof streams[ number ] )=> {
				const next = stream.bounded_pages.next()
				if( next.done ) { stream.pending = false; return false }
				const entries = next.value.page.entries() as readonly $shm_hitalama_map_history_summary[]
				stream.bound = next.value.bound
				// Pages arrive newest first, but their entries are appended oldest first.
				// Keep that durable stream order when several commands share a millisecond.
				for( let index = entries.length - 1; index >= 0; --index ) {
					const summary = entries[index], position = stream.position++
					if( selected.has( summary.e ) ) continue
					if( !summary.g.includes( target ) && !this.storage_summary_targets( summary ).includes( target ) ) continue
					selected.set( summary.e, summary )
					positions.set( summary.e, { writer: stream.writer, position } )
				}
				return true
			}
			while( true ) {
				const ordered = [ ... selected.values() ].sort( compare )
				const last = ordered[ wanted - 1 ]
				const threshold = last?.t ?? Number.NEGATIVE_INFINITY
				// An older page from the same writer cannot displace its loaded ties.
				// A competing writer with higher tie priority can, even at equal time.
				const pending = streams.filter( stream => stream.pending && (
					ordered.length < wanted || stream.bound > threshold || (
						stream.bound === threshold && last && stream.writer < positions.get( last.e )!.writer
					)
				) )
				if( !pending.length ) return { count, items: ordered }
				// Read only the stream with the highest possible next timestamp.
				// Once enough rows are found, old writers need no summary-page reads.
				pending.sort( ( a, b )=> a.bound === b.bound
					? ( a.writer < b.writer ? -1 : a.writer > b.writer ? 1 : 0 )
					: a.bound > b.bound ? -1 : 1 )
				load( pending[0] )
			}
		}

		static value( value: any ) {
			return $shm_hitalama_map_history_runtime.value( value )
		}

		static equal( left: any, right: any ) {
			return $shm_hitalama_map_history_runtime.equal( left, right )
		}

		static slot< Value >( owner: object | string | null, key: string, make: ()=> Value ) {
			return $shm_hitalama_map_history_runtime.slot( owner, key, make )
		}

		static transaction< Result >(
			map: $shm_hitalama_map | null | undefined,
			title: string,
			task: ()=> Result,
			source: $shm_hitalama_map_history_source = 'user',
			id?: string,
		) {
			return $shm_hitalama_map_history_runtime.transaction( map, title, task, source, id )
		}

		static capture(
			map: $shm_hitalama_map | null | undefined,
			change: $shm_hitalama_map_history_change,
			title = change.type,
			source: $shm_hitalama_map_history_source = 'user',
		) {
			return $shm_hitalama_map_history_runtime.capture( map, change, title, source )
		}

		static silence< Result >( task: ()=> Result ) {
			return $shm_hitalama_map_history_runtime.silence( task )
		}

		static is_muted() {
			return $shm_hitalama_map_history_runtime.is_muted()
		}

		static geometry_begin( widget: $shm_hitalama_map_widget ) {
			return $shm_hitalama_map_history_runtime.geometry_begin( widget )
		}

		static geometry_begin_many( widgets: readonly $shm_hitalama_map_widget[] ) {
			return $shm_hitalama_map_history_runtime.geometry_begin_many( widgets )
		}

		static geometry_active( widget: $shm_hitalama_map_widget ) {
			return $shm_hitalama_map_history_runtime.geometry_active( widget )
		}

		static geometry_end( widget: $shm_hitalama_map_widget ) {
			return $shm_hitalama_map_history_runtime.geometry_end( widget )
		}

		static geometry_end_map( map: $shm_hitalama_map ) {
			return $shm_hitalama_map_history_runtime.geometry_end_map( map )
		}

		static edit_begin( map: $shm_hitalama_map ) {
			return $shm_hitalama_map_history_runtime.edit_begin( map )
		}

		static edit_end( map: $shm_hitalama_map ) {
			return $shm_hitalama_map_history_runtime.edit_end( map )
		}

		@ $mol_action
		event_add( data: $shm_hitalama_map_history_data ) {
			const pass = this.$.$giper_baza_auth.current().pass()
			const peer = pass.peer().str
			const existing = this.storage_writer( peer )?.recent().find( item => item.i === data.id )
			if( existing ) return this.storage_command( peer, data.id, this.storage_event( existing.e ) )!
			data = $mol_wire_sync( this ).large_text_store( data )
			const time = $mol_wire_sync( Date ).now()
			const targets = $mol_wire_sync( this ).event_targets( data )
			return this.event_write( data, time, pass.lord().str, peer, targets )
		}

		event_targets( data: $shm_hitalama_map_history_data ) {
			return [ ... new Set( data.changes.flatMap( change => [
				change.target,
				... change.targets ?? [],
			] ).filter( Boolean ) ) ]
		}

		event_bytes(
			data: $shm_hitalama_map_history_data, time: number,
			author: string, peer: string, targets: readonly string[],
		) {
			return $mol_charset_encode( JSON.stringify({ time, author, peer, targets, data }) ).byteLength
		}

		@ $mol_action
		event_write(
			data: $shm_hitalama_map_history_data,
			time: number,
			author: string,
			peer: string,
			targets: readonly string[],
		) {
			this.storage_meta().version( $shm_hitalama_map_history.storage_version )
			const command = this.storage_command( peer, data.id )
			if( command ) return this.storage_command_resume( command )
			const writer_id = $shm_hitalama_map_history_runtime.writer_id( peer )
			const existing = this.storage_writer( writer_id )?.recent().find( item => item.i === data.id )
				?? this.storage_writer( peer )?.recent().find( item => item.i === data.id )
			if( existing ) return this.storage_command( peer, data.id, this.storage_event( existing.e ) )!
			const writer = this.storage_writer( writer_id, true )!
			const bytes = $mol_wire_sync( this ).event_bytes( data, time, author, peer, targets )
			const allocated = $mol_wire_sync( writer ).allocate(
				bytes,
				$shm_hitalama_map_history.storage_page_size,
				$shm_hitalama_map_history.storage_page_byte_limit,
				time,
			)
			const page = allocated.page
			const land = this.storage_payload_land( writer_id, page )
			let idea = $mol_hash_string( data.id )
			while( true ) {
				idea = $mol_hash_numbers([ idea ])
				if( !idea ) continue
				const head = $giper_baza_link.from_int( idea )
				if( /[æÆ]/.test( head.str ) ) continue
				const event = land.Pawn( $shm_hitalama_map_history_event ).Head( head )
				const normalized = $mol_wire_sync( $shm_hitalama_map_history_runtime ).value( data )
				if( !event.packed_header() ) event.packed({ v: 2, time, author, peer, targets, data: normalized })
				if( !event.storage() ) event.storage({ writer: writer_id, page })
				// Publish the immutable command address after its complete payload,
				// but before index checkpoints which can suspend. A later delivery
				// resumes indexing this event without allocating another payload page.
				this.storage_command( peer, data.id, event )
				return this.storage_index_event( event, normalized, time, author, peer, targets )
			}
		}

		@ $mol_action
		storage_migrate( force = false ) {
			if( !force && this.Legacy_migrated()?.val() ) return 0
			const legacy = [ ... this.Events()?.remote_list() ?? [] ].sort( ( left, right )=> left.time() - right.time() )
			const missing: string[] = []
			for( const event of legacy ) {
				event.land().sync()
				if( !event.data() ) missing.push( event.link().str )
			}
			if( missing.length ) {
				throw new Error( `Не загружены payload событий для миграции: ${ missing.length }` )
			}
			const batches = new Map< string, {
				writer: string
				page: number
				items: $shm_hitalama_map_history_summary[]
			} >
			const writers = new Map< string, {
				state: $shm_hitalama_map_history_storage_writer
				cursor: $shm_hitalama_map_history_storage_writer_cursor
				stored: Map< string, number >
			} >
			const writer_state = ( writer: string )=> {
				let value = writers.get( writer )
				if( value ) return value
				const state = this.storage_writer( writer, true )!
				const cursor = state.cursor()!
				const stored = new Map< string, number >
				for( let page = 0; page <= cursor.page; ++page ) {
					for( const summary of this.storage_summary_page( writer, page ).entries() ) stored.set( summary.i, page )
				}
				writers.set( writer, value = { state, cursor, stored })
				return value
			}
			const relocated = new Map< string, $shm_hitalama_map_history_event >
			const planned: {
				event: $shm_hitalama_map_history_event
				stored: $shm_hitalama_map_history_event
				writer: string
				page: number
			}[] = []
			for( const event of legacy ) {
				const data = event.data()
				if( !data ) continue
				const time = event.time()
				const author = event.author()
				const peer = event.peer()
				const storage_writer = `legacy:${ peer }`
				const targets = event.targets()
				const bytes = $mol_charset_encode( JSON.stringify({ time, author, peer, targets, data }) ).byteLength
				const writer = writer_state( storage_writer )
				let page = writer.stored.get( data.id )
				if( page === undefined ) {
					writer.cursor = writer.state.cursor_plan( writer.cursor, bytes,
						$shm_hitalama_map_history.storage_page_size,
						$shm_hitalama_map_history.storage_page_byte_limit, time )
					page = writer.cursor.page
					writer.stored.set( data.id, page )
				}
				// A compact index alone is not a migration: its `e` link would still
				// point into the old shared history land and opening details would pull
				// that land back in. Copy the immutable payload into the bounded page
				// first, then index the new link.
				const land = this.storage_payload_land( storage_writer, page )
				let idea = $mol_hash_string( data.id )
				let stored: $shm_hitalama_map_history_event | null = null
				while( !stored ) {
					idea = $mol_hash_numbers([ idea ])
					if( !idea ) continue
					const head = $giper_baza_link.from_int( idea )
					if( /[æÆ]/.test( head.str ) ) continue
					stored = land.Pawn( $shm_hitalama_map_history_event ).Head( head )
				}
				relocated.set( event.link().str, stored )
				planned.push({ event, stored, writer: storage_writer, page })
			}
			let migrated = 0
			for( const { event, stored, writer, page } of planned ) {
				const data = event.data()!
				const time = event.time(), author = event.author(), peer = event.peer(), targets = event.targets()
				const normalized = $mol_wire_sync( $shm_hitalama_map_history_runtime ).value({
					...data,
					...data.origin ? { origin: relocated.get( data.origin )?.link().str ?? data.origin } : {},
				})
				if( !stored.packed_header() ) stored.packed({ v: 2, time, author, peer, targets, data: normalized })
				if( !stored.storage() ) stored.storage({ writer, page })
				const key = `${ writer }:${ page }`
				let batch = batches.get( key )
				if( !batch ) batches.set( key, batch = { writer, page, items: [] })
				batch.items.push( this.storage_summary( stored, normalized, time, author, peer, targets ) )
				++migrated
			}

			const normalized_pages = new Map< string, readonly $shm_hitalama_map_history_summary[] >
			for( const batch of batches.values() ) {
				const page = this.storage_summary_page( batch.writer, batch.page )
				const current = page.entries()
				const normalized = new Map( current.map( summary => [ summary.i, summary ] ) )
				for( const summary of batch.items ) normalized.set( summary.i, summary )
				const updated = [ ... normalized.values() ]
				const changed = updated.length !== current.length || updated.some( ( summary, index )=> (
					JSON.stringify( summary ) !== JSON.stringify( current[ index ] )
				) )
				if( changed ) {
					let entries = page.Entries()
					if( !entries ) {
						page.Entries( null )
						entries = page.Entries()
					}
					if( !entries ) throw new Error( 'Не удалось открыть страницу миграции истории' )
					entries.items( updated )
				}
				normalized_pages.set( page.link().str, updated )
			}
			for( const [ writer, value ] of writers ) {
				value.state.cursor( value.cursor )
			}
			const writer_ids = new Set( this.Storage_writers()?.keys().map( String ) ?? [] )
			for( const writer of writers.keys() ) writer_ids.add( writer )
			this.storage_reindex_writers([ ... writer_ids ], normalized_pages )

			let version = this.Storage_version()
			if( !version ) {
				this.Storage_version( null )
				version = this.Storage_version()
			}
			version?.val( $shm_hitalama_map_history.storage_version )
			let migrated_state = this.Legacy_migrated()
			if( !migrated_state ) {
				this.Legacy_migrated( null )
				migrated_state = this.Legacy_migrated()
			}
			if( !migrated_state ) throw new Error( 'Не удалось записать состояние миграции истории' )
			migrated_state.val( true )
			this.storage_migrated( true )
			return migrated
		}

		private storage_reindex_writers(
			writer_ids: readonly string[],
			normalized_pages?: ReadonlyMap< string, readonly $shm_hitalama_map_history_summary[] >,
		) {
			const commands = new Map< string, $shm_hitalama_map_history_summary >
			const targets = new Map< string, Map< string, {
				count: number
				pages: Map< string, $shm_hitalama_map_history_storage_page >
			} > >
			for( const writer_id of writer_ids ) {
				const writer = this.storage_writer( writer_id, true )!
				const cursor = writer.cursor()!
				const recent: $shm_hitalama_map_history_summary[] = []
				const seen = new Set< string >
				for( let index = 0; index <= cursor.page; ++index ) {
					const page = this.storage_summary_page( writer_id, index )
					const summaries = normalized_pages?.get( page.link().str ) ?? page.entries()
					for( const summary of summaries ) {
						if( seen.has( summary.e ) ) continue
						seen.add( summary.e )
						commands.set( summary.e, summary )
						recent.push( summary )
						for( const target of this.storage_summary_targets( summary ) ) {
							let streams = targets.get( target )
							if( !streams ) targets.set( target, streams = new Map )
							let state = streams.get( writer_id )
							if( !state ) streams.set( writer_id, state = { count: 0, pages: new Map })
							++state.count
							state.pages.set( page.link().str, page )
						}
					}
				}
				writer.recent( recent.sort( ( left, right )=> left.t - right.t ).slice( -$shm_hitalama_map_history.storage_recent_size ) )
			}
			for( const [ target, streams ] of targets ) {
				const state = this.storage_target( target, true )!
				let writers = state.Writers()
				if( !writers ) {
					state.Writers( null )
					writers = state.Writers()
				}
				if( !writers ) throw new Error( 'Не удалось открыть индекс авторов истории при миграции' )
				for( const [ writer_id, value ] of streams ) {
					let stream = writers.key( writer_id )
					if( !stream ) {
						writers.key( writer_id, 'auto' )
						stream = writers.key( writer_id )
					}
					if( !stream ) throw new Error( 'Не удалось открыть индекс автора истории при миграции' )
					stream.count( value.count )
					stream.pages([ ... value.pages.values() ])
				}
			}
			// Only publish command addresses after the reconstructed counters and
			// page references. Migrated events did not pass storage_index_event;
			// mark their completed scopes so a later repeat cannot count them twice.
			for( const summary of commands.values() ) {
				const event = this.storage_event( summary.e )
				for( const target of this.storage_summary_targets( summary ) ) event.indexed_target_mark( target )
				this.storage_command( summary.p, summary.i, event )
			}
		}

		large_text_store( data: $shm_hitalama_map_history_data ) {
			const store = ( value: any, title: string ): any => {
				if( typeof value === 'string' ) {
					if( $shm_hitalama_file_text_size( value ) <= $shm_hitalama_file_text_inline_limit ) return value
					const file = this.map().file_make_stable( `history:${ $mol_hash_string( `${ title }\n${ value }` ) }` )
					if( !file ) return value
					$shm_hitalama_file_text_write( file, value, title )
					return {
						hitalama_text_file: file.link().str,
						size: $shm_hitalama_file_text_size( value ),
						preview: value.slice( 0, 160 ),
					} satisfies $shm_hitalama_map_history_text_file
				}
				if( Array.isArray( value ) ) return value.map( item => store( item, title ) )
				if( !value || value.constructor !== Object ) return value
				return Object.fromEntries( Object.entries( value ).map( ([ key, item ])=> [ key, store( item, title ) ] ) )
			}

			return {
				... data,
				changes: data.changes.map( ( change, index )=> {
					if( change.type !== 'widget_set' ) return change
					if( !( 'text' in ( change.before ?? {} ) ) && !( 'text' in ( change.after ?? {} ) ) ) return change
					return {
						... change,
						before: store( change.before, `history-${ data.id }-${ index }-before.txt` ),
						after: store( change.after, `history-${ data.id }-${ index }-after.txt` ),
					}
				}),
			}
		}

		undo_candidate() {
			return $shm_hitalama_map_history_undo.for( this ).undo_candidate()
		}

		redo_candidate() {
			return $shm_hitalama_map_history_undo.for( this ).redo_candidate()
		}

		undo() {
			return $shm_hitalama_map_history_undo.for( this ).undo()
		}

		redo() {
			return $shm_hitalama_map_history_undo.for( this ).redo()
		}

	}

}
